import React, { useEffect, useState } from "react";
import svg7 from "../../../../assets/svg/svg7.svg";
import svg33 from "../../../../assets/svg/svg33.svg";
import { Descriptions, Divider, Progress, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MonthlyProgressCard from "./MonthlyProgressCard";
import ReportsByDateTable from "./ReportsByDateTable";
import BreadCrumb from "../../../../layout/BreadCrumb";
import moment from "moment";
import { fetchData } from "../../../../features/fetch/fetchSlice";

function HeaderSkeleton() {
  return (
    <>
      <Skeleton.Input
        active
        size="small"
        style={{ width: 120, minWidth: 120, height: 12 }}
      />
      <div className="mt-[.63rem]">
        <Skeleton.Input
          active
          style={{ width: 320, minWidth: 320, height: 26 }}
        />
      </div>
      <div className="mt-[.62rem]">
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 2, width: ["92%", "58%"] }}
        />
      </div>

      <div className="mt-[1.25rem] flex lg:flex-row flex-col gap-y-2 lg:items-center gap-x-[.62rem] w-full">
        <Skeleton.Input
          active
          size="small"
          style={{ width: 120, minWidth: 120, height: 14 }}
        />
        <Skeleton.Input
          active
          size="small"
          style={{ width: 170, minWidth: 170, height: 14 }}
        />
        <div className="flex-1 w-full">
          <Skeleton.Input active block style={{ height: 14 }} />
        </div>
      </div>

      <div className="flex items-start lg:flex-row flex-col mt-[2.5rem] w-full">
        <div className="w-full flex flex-col gap-y-[1.15rem]">
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-x-[1rem]">
              <Skeleton.Input
                active
                size="small"
                style={{ width: 130, minWidth: 130, height: 14 }}
              />
              <Skeleton.Input
                active
                size="small"
                style={{ width: 150, minWidth: 150, height: 14 }}
              />
            </div>
          ))}
        </div>

        <div className="w-full flex flex-shrink flex-wrap gap-y-[1rem] gap-x-[1rem] mt-[1rem]">
          {[0, 1, 2].map((card) => (
            <div
              key={card}
              className="rounded-[.5rem] border border-[#D6E1F5] px-[.625rem] py-[1rem] h-auto bg-[#F7F7F7] w-auto lg:min-w-[190px]"
            >
              <Skeleton.Input
                active
                size="small"
                style={{ width: 120, minWidth: 120, height: 12 }}
              />
              <div className="mt-[1.44rem]">
                <Skeleton.Input
                  active
                  style={{ width: 140, minWidth: 140, height: 22 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MonthlyDetailList() {
  // One call for the whole header: latest monthly report, with main_projects
  // filling the gaps. Replaces project/fetch_projects (empty for most
  // projects) and main_projects/get.
  const url = "monthly_report/fetch_project_header";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeObjMontProj, rawHeader } = useSelector((state) => ({
    activeObjMontProj: state.obj.activeObj["monthlyProj"],
    rawHeader: state.fetch.data[url],
  }));
  // A failed or empty fetch is stored as [].
  const header = rawHeader && !Array.isArray(rawHeader) ? rawHeader : {};

  const [loading, setLoading] = useState(true);

  // Calculate time elapsed from start date to today in months and days
  const startDate = header?.startDate;
  const endDate = header?.endDate;

  let elapsedText = "--";
  let elapsedPercent = 0;

  if (startDate) {
    const start = moment(startDate);
    const end = endDate ? moment(endDate) : null;
    const isCompleted =
      header?.projectStatus?.toUpperCase() === "COMPLETED";
    const reference = isCompleted && end ? end : moment();

    const months = reference.diff(start, "months");
    const days = reference.diff(start.clone().add(months, "months"), "days");
    elapsedText = `${months} month${months !== 1 ? "s" : ""} ${days} day${
      days !== 1 ? "s" : ""
    }`;

    if (end) {
      const totalDuration = end.diff(start, "days");
      const elapsedDuration = reference.diff(start, "days");
      elapsedPercent =
        totalDuration > 0
          ? Math.round((elapsedDuration / totalDuration) * 100)
          : 0;
    }
  }

  const items1 = [
    {
      key: "1",
      label: "Start Date",
      children: (
        <div>
          {header?.startDate
            ? moment(header?.startDate)?.format("DD-MM-YYYY")
            : "--"}
        </div>
      ),
    },
    {
      key: "5",
      label: "Expected End Date",
      children: (
        <div>
          {header?.endDate
            ? moment(header?.endDate)?.format("DD-MM-YYYY")
            : "--"}
        </div>
      ),
    },
    {
      key: "2",
      label: "Time Elapsed",
      children: (
        <div className="flex items-center gap-x-2">
          <span>{elapsedText}</span>
          {endDate && (
            <span
              className="font-[500] text-[.75rem]"
              style={{ color: elapsedPercent > 100 ? "#B81B0E" : "#4FAD5B" }}
            >
              ({elapsedPercent}%)
            </span>
          )}
        </div>
      ),
    },
  ];
  async function fetchHeaderData() {
    await dispatch(
      fetchData({
        url: url,
        mrProjectId: activeObjMontProj?.projectId,
      })
    );
  }

  useEffect(() => {
    setLoading(true);
    fetchHeaderData().finally(() => setLoading(false));
  }, []);

  return (
    <>
      <BreadCrumb
        breadcrumbs={[
          { title: "Monthly Report", path: "/rob/project-progress" },
          { title: `Monthly Report Details ${header?.projectName ?? ""}` },
        ]}
      />
      <div className="white_card mt-[2.41rem]">
        {loading ? (
          <HeaderSkeleton />
        ) : (
          <>
            <div className="text-[#0092CC] font-interSans font-[500] text-[.75rem]">
              {header?.projectNumber}
            </div>
            <div className="form-add-h !text-[#050505] mt-[.63rem]">
              {header?.projectName}
            </div>
            <div className="proj_desc mt-[.62rem]">{header?.remarks}</div>
            <div className="mt-[1.25rem] flex lg:flex-row flex-col gap-y-2 lg:items-center gap-x-[.62rem] w-full">
              <div className="flex items-center gap-x-1">
                <img src={svg7} alt="svg7" />
                <span className="forgot-text">{header?.countyName}</span>
              </div>

              <div className="flex items-center gap-x-1 ">
                <img src={svg33} alt="svg33" />
                <span className="forgot-text"> {header?.agencyName}</span>
              </div>

              <div className="flex  items-center flex-1 gap-x-[.62rem]">
                <span className="forgot-text lg:flex hidden">| Progress</span>
                <Progress
                  strokeColor="#4FAD5B"
                  className="lg:w-[30%] w-full"
                  percent={Number(header?.progress) || 0}
                />
              </div>
            </div>

            <div className="flex items-center lg:flex-row flex-col mt-[2.5rem] w-full">
              <div className="w-full">
                <Descriptions column={1} colon={false} items={items1} />
              </div>

              <div className="w-full">
                <MonthlyProgressCard />
              </div>
            </div>
          </>
        )}

        <div className="mt-[2.44rem]">
          <Divider />
        </div>
        <ReportsByDateTable />
      </div>
    </>
  );
}

export default MonthlyDetailList;
